import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card } from '@playerbase/ui';

import { useAuth } from '../auth/use-auth.js';
import { supabase } from '../lib/supabase.js';
import {
  completeDiscordConnect,
  type CompleteDiscordConnectError,
} from '../games/discord-data.js';

// Landing route for Discord's OAuth redirect_uri. Discord redirects here
// with code/state/guild_id query params (or error/error_description on
// denial). We finish the round-trip via the discord-oauth edge function and
// navigate the user back into the originating game's Discord connection.
//
// This route lives outside the studio guard because the redirect lands at a
// stable URL regardless of which studio the connect flow began from. We use
// the auth context to recover the studio slug from the response, and look
// the game up directly from Postgres to recover the game slug. RLS scopes
// both reads to the caller's memberships.

type Phase =
  | { kind: 'loading' }
  | { kind: 'redirecting'; href: string }
  | { kind: 'denied'; reason: string }
  | { kind: 'blocked' }
  | { kind: 'error'; code: CompleteDiscordConnectError };

function errorMessage(code: CompleteDiscordConnectError): string {
  switch (code) {
    case 'invalid_state':
    case 'state_user_mismatch':
    case 'state_expired':
      return 'This connect link expired or was used in a different browser. Try connecting again from the Discord connection page.';
    case 'state_missing_game':
      return 'The connect link did not carry a game. Try connecting again from the Discord connection page.';
    case 'guild_mismatch':
      return 'Discord returned a different server than expected. For your safety the connection was not saved. Try again.';
    case 'discord_token_exchange_failed':
      return 'Discord did not accept the consent code. Try again from the Discord connection page.';
    case 'discord_guild_lookup_failed':
      return 'Discord accepted the install but could not return the server details. Try again - the bot is already in the server, so reconnecting is safe.';
    case 'game_not_found':
      return 'The game this connection belonged to was deleted. Reload the portal and try again.';
    case 'server_misconfigured':
      return 'Discord OAuth is not configured for this environment. Tell whoever set up the project to fill in DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET.';
    case 'persist_failed':
      return 'Could not save the connection right now. Try again in a moment.';
    case 'not_authenticated':
      return 'Your session expired during the Discord round-trip. Sign in and try again.';
    case 'unknown':
    default:
      return 'Something went wrong finishing the Discord connection. Try again from the Discord connection page.';
  }
}

export function OAuthDiscordCallbackPage(): JSX.Element {
  const [params] = useSearchParams();
  const { studios } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  // Guard against double-runs from React StrictMode and HMR. The state row
  // is single-use, so a second run would produce 'invalid_state' even on a
  // perfectly happy callback.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const guildId = params.get('guild_id');
    const oauthError = params.get('error');

    if (oauthError !== null) {
      setPhase({
        kind: 'denied',
        reason:
          params.get('error_description') ??
          'You cancelled or did not approve the bot install on Discord.',
      });
      return;
    }

    if (code === null || state === null || guildId === null) {
      setPhase({ kind: 'error', code: 'unknown' });
      return;
    }

    void (async () => {
      const result = await completeDiscordConnect({ code, state, guildId });
      if (!result.ok) {
        setPhase({ kind: 'error', code: result.code });
        return;
      }
      if (result.response.outcome === 'blocked_other_studio') {
        setPhase({ kind: 'blocked' });
        return;
      }

      // Navigate back to the originating game's Discord connection. Recover
      // the studio short id from auth context (the user is signed in already)
      // and the game short id via a direct row lookup.
      const studioId = result.response.connection.studioId;
      const gameId = result.response.gameId;
      const studio = studios.find((s) => s.id === studioId);
      const studioShortId = studio?.shortId;

      let gameShortId: string | null = null;
      const { data: gameRow } = await supabase
        .from('games')
        .select('short_id')
        .eq('id', gameId)
        .single();
      if (gameRow !== null && typeof gameRow.short_id === 'string') {
        gameShortId = gameRow.short_id;
      }

      if (studioShortId !== undefined && gameShortId !== null) {
        const target = `/s/${studioShortId}/g/${gameShortId}/game-settings/discord`;
        // Hard reload via location.href ensures the AppShell mounts fresh,
        // shell context loads against the new game, and the Discord page
        // renders the just-created connection row (which Realtime will keep
        // current as the bot confirms).
        setPhase({ kind: 'redirecting', href: target });
        window.location.assign(target);
        return;
      }
      setPhase({ kind: 'error', code: 'unknown' });
    })();
  }, [params, studios]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <Card.Header>
          <Card.Title>Finishing Discord connection</Card.Title>
          <Card.Description>
            {phase.kind === 'loading'
              ? 'Talking to Discord and saving the connection…'
              : phase.kind === 'redirecting'
                ? 'Redirecting back to your Discord connection…'
                : phase.kind === 'denied'
                  ? 'You cancelled the bot install on Discord.'
                  : phase.kind === 'blocked'
                    ? 'This Discord server is already connected to another PlayerBase studio.'
                    : 'We could not finish the connection.'}
          </Card.Description>
        </Card.Header>
        <Card.Content>
          {phase.kind === 'loading' && (
            <p className="text-sm text-text-muted">One moment…</p>
          )}

          {phase.kind === 'denied' && (
            <Alert tone="info">{phase.reason}</Alert>
          )}

          {phase.kind === 'blocked' && (
            <Alert tone="danger">
              This Discord server is already connected to PlayerBase. Contact
              support if this is unexpected.
            </Alert>
          )}

          {phase.kind === 'error' && (
            <Alert tone="danger">{errorMessage(phase.code)}</Alert>
          )}
        </Card.Content>
        {(phase.kind === 'denied' || phase.kind === 'blocked' || phase.kind === 'error') && (
          <Card.Footer>
            <Button asChild variant="primary" size="md">
              <Link to="/">Back to PlayerBase</Link>
            </Button>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
}
